import { InfoIcon } from '@/assets/icons/info-icons/info';
import { Banner } from '@/components/basicComponents/banner';
import DatePicker from '@/components/basicComponents/datePicker';
import Input from '@/components/basicComponents/input';
import Select from '@/components/basicComponents/select';
import { useResponsive } from '@/hooks/use-responsive';
import { themeVar } from '@/styles/theme';
import { COUNTRIES } from '@trace-8/trace8-schema';
import { buildTestId } from '@/lib/utils/testIds';
import React, { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled, { useTheme } from 'styled-components';
import { IRenderTemplateField, IRenderTemplateRow } from '../../types';
import { evaluateDependencyItem, shouldRenderEmptyComponent } from '../../utils';
import { Checkbox } from './checkbox';
import { PhoneNumber } from './phoneNumber';
import { RadioComponent } from './radioComponent';

interface CustomComponentProps {
  row: IRenderTemplateRow;
  pageName?: string;
  ignoreRequired: boolean;
}

export const CustomComponent = ({ row, pageName = '', ignoreRequired }: CustomComponentProps) => {
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const { control, unregister, watch } = useFormContext();
  const { dependsOn } = row;
  const dependentValue = dependsOn ? watch(`${pageName}.${dependsOn.name}`) : null;
  const hasValidOrDependency: boolean = dependsOn?.or
    ? dependsOn.or.some((item: any) => {
        const dependentValue = watch(`${pageName}.${item.name}`);
        return evaluateDependencyItem(dependentValue, item);
      })
    : null;
  const hasValidAndDependency: boolean = dependsOn?.and
    ? dependsOn.and.every((item: any) => {
        const dependentValue = watch(`${pageName}.${item.name}`);
        return evaluateDependencyItem(dependentValue, item);
      })
    : null;

  useEffect(() => {
    if (shouldRenderEmptyComponent(dependsOn, dependentValue, hasValidOrDependency, hasValidAndDependency)) {
      row.fields.forEach((field) => {
        unregister(`${pageName}.${field.name}`);
      });
    }
  }, [dependentValue, hasValidOrDependency, hasValidAndDependency]);

  if (shouldRenderEmptyComponent(dependsOn, dependentValue, hasValidOrDependency, hasValidAndDependency)) return <></>;

  const getCurrentField = (field: IRenderTemplateField) => {
    switch (field.component) {
      case 'checkbox':
        return <Checkbox field={field} control={control} pageName={pageName} ignoreRequired={ignoreRequired} />;
      case 'date':
        return (
          <Controller
            name={`${pageName}.${field.name}`}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <DatePicker
                label={field?.label || ''}
                value={value || ''}
                onChange={(date) => {
                  onChange(date ? date : '');
                }}
                errorText={error?.message}
                isRequiredField={!!field?.validation?.required && !ignoreRequired}
                size="md"
                data-testid={field.name ? buildTestId('signature-packet', pageName, field.name) : undefined}
              />
            )}
          />
        );
      case 'phoneNumber':
        return <PhoneNumber field={field} control={control} pageName={pageName} ignoreRequired={ignoreRequired} />;
      case 'select':
        return (
          <Controller
            name={`${pageName}.${field.name}`}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Select
                label={field?.label || ''}
                value={value || ''}
                options={[
                  { label: 'Select', value: '' },
                  ...(field?.validation?.type === 'country'
                    ? COUNTRIES.map((country) => ({ label: country, value: country }))
                    : field?.config?.items || [])
                ]}
                setValue={onChange}
                errorText={error?.message}
                isRequiredField={!!field?.validation?.required && !ignoreRequired}
                withSearch
                size="md"
                data-testid={field.name ? buildTestId('signature-packet', pageName, field.name) : undefined}
                data-testid-dropdown={field.name ? buildTestId('signature-packet', pageName, field.name, 'dropdown') : undefined}
                data-testid-option={field.name ? buildTestId('signature-packet', pageName, field.name, 'option') : undefined}
              />
            )}
          />
        );
      case 'text':
        return (
          <Controller
            name={`${pageName}.${field.name}`}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                label={field?.label || ''}
                value={value || ''}
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                errorText={error?.message}
                isRequiredField={!!field?.validation?.required && !ignoreRequired}
                size="md"
                data-testid={field.name ? buildTestId('signature-packet', pageName, field.name) : undefined}
              />
            )}
          />
        );
      case 'infoBlock':
        return (
          <Banner
            title=""
            description={field?.config?.content || ''}
            icon={<InfoIcon width={26} height={26} fill={themeVar('header.base')} />}
            bgColor={themeVar('layer.base')}
            color={themeVar('font.base')}
          />
        );
      case 'radio':
        return <RadioComponent field={field} pageName={pageName} ignoreRequired={ignoreRequired} />;
      default:
        return <></>;
    }
  };

  if (row.fields.length === 2 && row.fields.some((field) => field.component === 'infoBlock')) {
    return (
      <TwoColumnsRow isMobile={isMobile}>
        {row.fields.map((field, index) => (
          <React.Fragment key={field.name + index}>{getCurrentField(field)}</React.Fragment>
        ))}
      </TwoColumnsRow>
    );
  }

  return (
    <RowWrapper>
      {row.fields.map((field, index) => (
        <React.Fragment key={field.name + index}>{getCurrentField(field)}</React.Fragment>
      ))}
    </RowWrapper>
  );
};

const RowWrapper = styled.div`
  display: flex;
  gap: 30px;
`;

const TwoColumnsRow = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr' : '1fr 1fr')};
  gap: ${({ isMobile }) => (isMobile ? '15px' : '30px')};
`;
